import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { RowInner } from './AppNavLinks'
import type { NavItem } from './navItems'
import styles from './AppNavLinks.module.css'

/**
 * The single shared row + group primitives for the polished, icon'd nav idiom
 * (mono-uppercase group labels, per-item line icons, the inset active pill).
 *
 * Both the mobile drawer/sheet (DrawerNavContent) and the desktop avatar
 * dropdown (PublicAccountMenu) render these exact pieces, so the two surfaces
 * are literally one source and can never drift. Styles live in
 * AppNavLinks.module.css; icons in navIcons.
 *
 * `menu` switches on the ARIA roles a `role="menu"` popover needs (the desktop
 * dropdown): the row becomes a `menuitem` and its list wrappers `none`, so the
 * grouped structure exposes only the interactive rows to assistive tech. The
 * mobile drawer leaves `menu` off and stays a plain labelled nav list.
 */

type RowProps = {
  item: NavItem
  /** Called after the row activates, so a host drawer/sheet/menu closes itself. */
  onNavigate?: () => void
  /** Render as a `role="menuitem"` inside a `role="menu"` popover. */
  menu?: boolean
}

/** One icon'd nav row, honouring the item's custom active predicate. */
export function NavItemRow({ item, onNavigate, menu = false }: RowProps) {
  const { pathname } = useLocation()
  const active = item.isActive?.(pathname)
  return (
    <li className={styles.item} role={menu ? 'none' : undefined}>
      <NavLink
        to={item.href}
        end={item.href === '/'}
        onClick={onNavigate}
        role={menu ? 'menuitem' : undefined}
        className={({ isActive: defaultActive }) =>
          `${styles.link} ${(active ?? defaultActive) ? styles.linkActive : ''}`.trim()
        }
        aria-current={active === false ? false : 'page'}
      >
        <RowInner icon={item.icon} label={item.label} />
      </NavLink>
    </li>
  )
}

type GroupProps = {
  label: string
  items: NavItem[]
  onNavigate?: () => void
  /** Draw the group separator above (every group but the first in a column). */
  leadDivider?: boolean
  /** Render the rows with `role="menuitem"` for a `role="menu"` popover. */
  menu?: boolean
  /** Extra rows appended after the item list (e.g. the Sign out button). */
  children?: ReactNode
}

/** A labelled group of icon'd rows (mono-uppercase label + inset active pills). */
export function NavGroup({
  label,
  items,
  onNavigate,
  leadDivider = false,
  menu = false,
  children,
}: GroupProps) {
  return (
    <>
      {leadDivider ? <hr className={styles.divider} /> : null}
      <div className={styles.groupLabel} aria-hidden="true">
        {label}
      </div>
      <ul className={styles.list} aria-label={label} role={menu ? 'none' : undefined}>
        {items.map((item) => (
          <NavItemRow key={item.href} item={item} onNavigate={onNavigate} menu={menu} />
        ))}
        {children}
      </ul>
    </>
  )
}
